import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { OnboardingFormData, SignupStep } from "../../api/getOnboardingInitialData";
import { getAffiliateDataFromRegistrationLink } from "~/domains/affiliate/utils/affiliateHelper";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { putCompleteRegistration } from "../../api/putCompleteAccount";
import { useOnMount } from "~/common/hooks/useOnMount";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useOnboardingOverlay } from "../../hooks/OnboardingOverlayContext";
import type { AFFILIATE_FORM_KEY, AffiliateForm } from "~/domains/affiliate/types/form";
import { CURRENT_LANGUAGE } from "~/utils/constants";
import type { DropdownEntry } from "~/common/types";
import type { LoginAfterRegistration } from "../../hooks/OnboardingOverlayContext";
export interface OnboardingFormValues {
  title: string;
  salutation: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  company: string;
  industrySector: string;
  jobTitle: string;
  functionalArea: string;
  address: string;
  addressAdditional: string;
  zipCode: string;
  city: string;
  country: string;
  phone: string;
  language: string;
  knowledgeArea: string;
  customerCategory: string;
  vat: string;
  termsTraining: boolean;
  [AFFILIATE_FORM_KEY]?: AffiliateForm;
}

export function useOnboardingForm({
  data,
  steps,
  languageOptions,
  loginAfterRegistration,
}: {
  data: OnboardingFormData;
  steps: SignupStep[];
  languageOptions: DropdownEntry<string>[];
  loginAfterRegistration: LoginAfterRegistration;
}) {
  const { t } = useTranslation();

  const { closeOnboarding } = useOnboardingOverlay();
  const activationCompleted = useRef(false);
  const submissionInFlight = useRef<Promise<void> | null>(null);

  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [isValidLink, setIsValidLink] = useState(data.validLink);
  const [globalErrors, setGlobalErrors] = useState<string[]>([]);
  const { val: isExitDialogOpen, setTrue: openExitDialog, setFalse: closeExitDialog } = useBooleanState(false);

  const { affiliateId, affiliateAccepted, selectedSubsidiary } = useMemo(getAffiliateDataFromRegistrationLink, []);

  const formProps = useForm<OnboardingFormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      salutation: "",
      firstName: "",
      lastName: "",
      company: "",
      industrySector: "",
      jobTitle: "",
      address: "",
      addressAdditional: "",
      zipCode: data.zipCode ?? "",
      city: "",
      country: data.selectedCountry ?? "",
      password: "",
      confirmPassword: "",
      language: languageOptions.some(({ value }) => value === CURRENT_LANGUAGE) ? CURRENT_LANGUAGE : "en",
      customerCategory: "",
      knowledgeArea: "",
      email: data.email,
      affiliate: {
        id: affiliateAccepted ? affiliateId ?? "" : "",
        subsidiary: selectedSubsidiary ?? "",
      },
    },
  });

  const {
    formState: { errors },
    watch,
  } = formProps;

  watch();
  useOnMount(() => {
    formProps.register("email");
    formProps.register("affiliate.id");
  });

  const isLastStep = activeStep === steps.length - 1;

  const values = formProps.watch();

  const completeRegistrationAndLogin = async (values: OnboardingFormValues) => {
    if (!activationCompleted.current) {
      const response = await putCompleteRegistration({
        ...values,
        passwordToken: data.token,
        affiliateId: affiliateId ?? undefined,
      });

      if (Object.keys(response.validationResults).length > 0) {
        Object.entries(response.validationResults).forEach(([fieldName, errs], index) => {
          if (!errs || errs.length === 0) {
            return;
          }

          formProps.setError(
            fieldName as keyof OnboardingFormValues,
            {
              type: errs[0],
              message: t(errs[0]),
            },
            { shouldFocus: index === 0 },
          );
        });
        return;
      }

      if (response.globalErrors.length > 0) {
        setIsValidLink(false);
        setGlobalErrors(response.globalErrors);
        return;
      }

      activationCompleted.current = true;
    }

    const loginSucceeded = await loginAfterRegistration(values.email, values.password);
    if (!loginSucceeded) {
      return;
    }

    setCompleted(true);
    formProps.reset(formProps.getValues());
  };

  const onSubmitHandler = (values: OnboardingFormValues) => {
    if (!submissionInFlight.current) {
      submissionInFlight.current = completeRegistrationAndLogin(values).finally(() => {
        submissionInFlight.current = null;
      });
    }
    return submissionInFlight.current;
  };

  const handleNext = async () => {
    const fieldNames = currentStep.fields
      .flatMap((field) => (Array.isArray(field) ? field : [field]))
      .map((field) => field.name);

    const valid = await formProps.trigger(fieldNames);

    if (!valid || isLastStep) {
      return;
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCloseOnboarding = () => {
    closeOnboarding();
    redirectToUrl(HOMEPAGE);

    setTimeout(() => {
      setActiveStep(0);
      setCompleted(false);
    }, 200);
  };

  const handleConfirmExit = () => {
    formProps.reset();
    handleCloseOnboarding();
    closeExitDialog();
  };

  const currentStep = steps[activeStep];
  const isSkippable = currentStep.skipable;

  const isStepComplete = useMemo(() => {
    const fields = currentStep.fields.flatMap((field) => (Array.isArray(field) ? field : [field]));

    const isValid = fields.every((field) => {
      const value = values[field.name];

      const hasValue = typeof value === "string" ? value.trim().length > 0 : value != null;

      if (!field.rules?.required && !hasValue) {
        return true;
      }

      return hasValue && !errors[field.name];
    });

    const isPasswordStep = fields.some((field) => field.name === "confirmPassword");
    const passwordsMatch = !isPasswordStep || values.password === values.confirmPassword;

    return isValid && passwordsMatch;
  }, [currentStep, values, errors]);

  return {
    formProps,
    activeStep,
    currentStep,
    isLastStep,
    completed,
    isValidLink,
    globalErrors,
    isStepComplete,
    isSkippable,
    isExitDialogOpen,
    closeExitDialog,
    openExitDialog,
    handleBack,
    handleNext,
    onSubmitHandler,
    handleConfirmExit,
  };
}
